'use client';

import { useEffect, useState } from 'react';
import HeroSection      from './HeroSection';
import SkillSection     from './SkillSection';
import ProjectSection   from './ProjectSection';
import ExperienceSection from './ExperienceSection';
import EducationSection from './EducationSection';
import AchievementsSection from './AchievementsSection';
import ContactSection   from './ContactSection';
import Footer           from '@/components/Footer';
import { trackCtaClick } from '@/lib/analytics';

const SECTION_IDS = ['hero', 'skills', 'projects', 'experience', 'education', 'achievements', 'contact', 'footer'];
const SECTION_LABELS: Record<string, string> = {
  hero: 'HERO',
  skills: 'SKILLS',
  projects: 'PROJECTS',
  experience: 'EXPERIENCE',
  education: 'EDUCATION',
  achievements: 'ACHIEVEMENTS',
  contact: 'CONTACT',
  footer: 'FOOTER',
};

export default function HomepageInteractive() {
  const [activeSection, setActiveSection] = useState('hero');

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const root = document.documentElement;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      root.style.setProperty('--scroll-pct', '0');
      return;
    }

    let rafId = 0;
    const updateScrollFx = () => {
      const maxScroll = Math.max(document.body.scrollHeight - window.innerHeight, 1);
      const progress = Math.min(Math.max(window.scrollY / maxScroll, 0), 1);

      root.style.setProperty('--scroll-pct', progress.toFixed(4));
      rafId = 0;
    };

    const onScroll = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(updateScrollFx);
    };

    updateScrollFx();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const items = Array.from(document.querySelectorAll<HTMLElement>('.scroll-reveal'));
    if (!items.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('revealed');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.14, rootMargin: '0px 0px -8% 0px' },
    );

    items.forEach((item, index) => {
      if (index === 0) {
        item.classList.add('revealed');
      } else {
        observer.observe(item);
      }
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const sections = Array.from(document.querySelectorAll<HTMLElement>('.scroll-section-track'));
    if (!sections.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (!visible.length) return;
        const sectionId = visible[0].target.getAttribute('data-section-id');
        if (sectionId) setActiveSection(sectionId);
      },
      { threshold: [0.25, 0.5, 0.75], rootMargin: '-20% 0px -20% 0px' },
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (sectionId: string) => {
    const target = document.querySelector<HTMLElement>(`[data-section-id="${sectionId}"]`);
    if (!target) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  };

  return (
    <div className="scroll-lite">
      {/* Global scanlines */}
      <div className="scanlines" aria-hidden="true" />
      <div className="scroll-vignette" aria-hidden="true" />
      <div className="scroll-top-bar" aria-hidden="true" />
      <div className="conversion-cta-dock">
        <a
          href="#contact"
          onClick={() => trackCtaClick('hire_me_dock', 'sticky_dock', '#contact')}
          className="conversion-cta-primary"
        >
          HIRE_ME
        </a>
        <a
          href="mailto:niranjanlangade@gmail.com"
          onClick={() =>
            trackCtaClick('quick_email_dock', 'sticky_dock', 'mailto:niranjanlangade@gmail.com')
          }
          className="conversion-cta-secondary"
        >
          QUICK_EMAIL
        </a>
      </div>
      <aside className="scroll-minimap" aria-label="Section progress">
        {SECTION_IDS.map((sectionId, index) => (
          <button
            key={sectionId}
            type="button"
            className={`scroll-minimap-dot ${activeSection === sectionId ? 'is-active' : ''}`}
            onClick={() => scrollToSection(sectionId)}
            aria-label={`Go to ${sectionId} section`}
            title={sectionId.toUpperCase()}
            style={{ transitionDelay: `${index * 20}ms` }}
          >
            <span className="scroll-minimap-label" aria-hidden="true">
              {SECTION_LABELS[sectionId]}
            </span>
          </button>
        ))}
      </aside>

      <div className="scroll-section-track reveal-item scroll-reveal hidden-initial" data-section-id="hero" style={{ transitionDelay: '0ms' }}>
        <HeroSection />
      </div>
      <div className="scroll-section-track reveal-item scroll-reveal hidden-initial" data-section-id="skills" style={{ transitionDelay: '60ms' }}>
        <SkillSection />
      </div>
      <div className="scroll-section-track reveal-item scroll-reveal hidden-initial" data-section-id="projects" style={{ transitionDelay: '120ms' }}>
        <ProjectSection />
      </div>
      <div className="scroll-section-track reveal-item scroll-reveal hidden-initial" data-section-id="experience" style={{ transitionDelay: '180ms' }}>
        <ExperienceSection />
      </div>
      <div className="scroll-section-track reveal-item scroll-reveal hidden-initial" data-section-id="education" style={{ transitionDelay: '240ms' }}>
        <EducationSection />
      </div>
      <div className="scroll-section-track reveal-item scroll-reveal hidden-initial" data-section-id="achievements" style={{ transitionDelay: '300ms' }}>
        <AchievementsSection />
      </div>
      <div className="scroll-section-track reveal-item scroll-reveal hidden-initial" data-section-id="contact" style={{ transitionDelay: '360ms' }}>
        <ContactSection />
      </div>
      <div className="scroll-section-track reveal-item scroll-reveal hidden-initial" data-section-id="footer" style={{ transitionDelay: '420ms' }}>
        <Footer />
      </div>
    </div>
  );
}
